// Statements — Sprint 13 orchestrator. Now a thin layout shell:
//  * jsPDF rendering         → generateStatementPDF (~325 LOC)
//  * data + persistence      → useStatementsData
//  * pure helpers            → statementHelpers (filter, summarize,
//                              monthOptions, quarterOptions,
//                              resolveActivePeriod)
//  * presentation            → StatementsHeader + StatementsStatsCards
//                              + StatementsFilterBar + StatementsTable
//  * preview modal           → StatementPreviewModal
// The orchestrator only owns transient UI state (filter mode, the
// current preview row, search text).
import React, { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAuthStore } from '../store/authStore';
import { useStatementsData }  from './statements/useStatementsData';
import { StatementsHeader }   from './statements/StatementsHeader';
import { StatementsStatsCards } from './statements/StatementsStatsCards';
import { StatementsFilterBar } from './statements/StatementsFilterBar';
import { StatementsTable }    from './statements/StatementsTable';
import { StatementPreviewModal } from './statements/StatementPreviewModal';
import {
  filterStatements, resolveActivePeriod, summarizeStatements, type FilterMode,
} from './statements/statementHelpers';

export const Statements: React.FC = () => {
  const { currentCondo } = useAuthStore();

  const {
    statements, owners,
    refresh, sendBulk, sendSingle, downloadPDF,
    isSending,
  } = useStatementsData({
    condoId:   currentCondo?.id,
    condoName: currentCondo?.name,
  });

  // ── Transient UI state ──────────────────────────────────────────────
  // Sprint 146 (R14-13) — default was 'monthly' @ current month, which hid 10
  // of 11 statements (every period-less one). Default to showing everything.
  const [filterMode,      setFilterMode]      = useState<FilterMode>('all');
  const [selectedMonth,   setSelectedMonth]   = useState(dayjs().format('YYYY-MM'));
  const [selectedQuarter, setSelectedQuarter] = useState(
    `${dayjs().year()}-Q${Math.ceil((dayjs().month() + 1) / 3)}`,
  );
  const [dateRange,   setDateRange]   = useState<[Dayjs, Dayjs] | null>(null);
  const [searchText,  setSearchText]  = useState('');
  const [previewStmt, setPreviewStmt] = useState<any>(null);

  // ── Derived data ────────────────────────────────────────────────────
  const periodStatements = useMemo(() => filterStatements(statements, owners, {
    mode:            filterMode,
    selectedMonth,
    selectedQuarter,
    dateRange,
    searchText,
    condoId:         currentCondo?.id,
  }), [statements, owners, filterMode, selectedMonth, selectedQuarter, dateRange, searchText, currentCondo?.id]);

  const stats        = useMemo(() => summarizeStatements(periodStatements), [periodStatements]);
  const activePeriod = resolveActivePeriod(filterMode, selectedMonth, selectedQuarter);

  const previewOwner = previewStmt
    ? owners.find((o: any) => o.id === previewStmt.ownerId || o.userId === previewStmt.ownerId)
    : null;

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0 4px' }}>
      <StatementsHeader
        draftCount={stats.draftCount}
        isSending={isSending}
        onRefresh={refresh}
        onSendBulk={() => sendBulk(activePeriod)}
      />

      <StatementsStatsCards
        count={stats.count}
        sentCount={stats.sentCount}
        draftCount={stats.draftCount}
        totalBalance={stats.totalBalance}
      />

      <StatementsFilterBar
        mode={filterMode}
        selectedMonth={selectedMonth}
        selectedQuarter={selectedQuarter}
        dateRange={dateRange}
        searchText={searchText}
        onModeChange={setFilterMode}
        onMonthChange={setSelectedMonth}
        onQuarterChange={setSelectedQuarter}
        onRangeChange={setDateRange}
        onSearchChange={setSearchText}
      />

      <StatementsTable
        rows={periodStatements}
        owners={owners}
        totalBalance={stats.totalBalance}
        onPreview={setPreviewStmt}
        onDownload={downloadPDF}
        onSendSingle={sendSingle}
        onRefresh={refresh}
      />

      {/* Sprint 146 (R14-4) — no `fees` prop: the modal renders
          `statement.fees`, the server's own itemisation. */}
      <StatementPreviewModal
        statement={previewStmt}
        owner={previewOwner}
        onClose={() => setPreviewStmt(null)}
        onDownload={downloadPDF}
        onSend={sendSingle}
      />
    </div>
  );
};
